<script setup lang="ts">
    //// IMPORTS
    import { computed, ref, watch } from "vue";
    import { useDebounce } from '@vueuse/core';
    import PageHeader from "@/components/common/PageHeader.vue";
    import PaymentsHistorySectionGrid from "@/components/Payments/PaymentsHistorySectionGrid.vue";
    import PaymentsHistorySearchForm from "@/components/Payments/PaymentsHistorySearchForm.vue";
    import OfficeSelector from "@/components/common/OfficeSelector.vue";
    import { Office, PaymentTransactionsIndexModel, SearchFilters } from "@/typings";
    import { applyInitialFilters, trimFilters } from "@/composables/search-utils";
    import { parseNumberList, readFiltersFromUrl, syncFiltersToUrl } from "@/composables/url-filters";
    import { CmnhInput, CmnhButton } from "@cmnhospitals/components-vue";
    import {
        getDefaultSearchFilters,
        aiLog,
        aiLogError,
    } from "@/utils"; // telemetry functions

    import Spinner from '@/components/common/Spinner.vue';
    import Alert from '@/components/common/Alert.vue';

    //// PROPS
    const props = defineProps<{
        paymentsData: PaymentTransactionsIndexModel;
        canModifyPayments?: boolean;
        offices: Office[];
        canViewAllOffices?: boolean;
        showAllOffices?: boolean;
        selectedOfficeId?: number;
    }>();

    //// URL-PERSISTED FILTERS
    // locationNumbers is derived from the office selector, which persists its own selection
    // (officeIds in the URL, selectedOfficeIds in localStorage), so it is not round-tripped itself.
    const URL_EXCLUDED_KEYS = new Set(["locationNumbers"]);
    const OFFICE_IDS_URL_KEY = "officeIds";
    const OFFICE_IDS_STORAGE_KEY = "selectedOfficeIds";

    const initialUrlFilters = readFiltersFromUrl();

    // Only keys the search form actually owns count as advanced filters, so an
    // unrelated query param can't open the panel on a link that restores nothing.
    // transactionNumber is excluded because it is the quick-search field.
    const ADVANCED_FILTER_KEYS = Object.keys(getDefaultSearchFilters())
        .filter(key => key !== "transactionNumber" && !URL_EXCLUDED_KEYS.has(key));

    const hasRestoredAdvancedFilters = ADVANCED_FILTER_KEYS.some(key => initialUrlFilters[key]);

    /** Strips the keys the URL does not own before writing a filter shape out. */
    function toUrlFilters(filters: Record<string, unknown>): Record<string, string> {
        return Object.fromEntries(
            Object.entries(filters)
                .filter(([key]) => !URL_EXCLUDED_KEYS.has(key))
                .map(([key, value]) => [key, value == null ? "" : String(value)])
        );
    }

    //// STATE VARIABLES (Refs)
    const officesSelected = ref<number[]>([]);
    const showAdvancedSearch = ref(hasRestoredAdvancedFilters);
    const searchFilters = ref<SearchFilters>(applyInitialFilters(getDefaultSearchFilters(), initialUrlFilters));

    interface GridApi {
        getPage(): number;
        getPageSize(): number;
        getSorters(): { field: string; dir: "asc" | "desc" }[];
    }

    const gridRef = ref<GridApi | null>(null);
    const totalRecords = ref(0);

    const isSearching = ref(true);
    const isExporting = ref(false);

    const quickSearch = ref(initialUrlFilters.transactionNumber ?? '');
    const debouncedQuickSearch = useDebounce(quickSearch, 1000);

    const exportError = ref<string | null>(null);

    //// OFFICE SELECTION
    // Every office selected (or the legacy -1 marker) is "All My Offices", which the server
    // treats as no office filter and scopes by the user's permissions instead.
    const isAllOfficesSelected = computed(() => {
        const ids = officesSelected.value;
        return ids.length === 0 || ids.includes(-1) || props.offices.every(office => ids.includes(office.id));
    });

    /** Comma-separated office numbers for the search request; empty when every office is selected. */
    const locationNumbers = computed(() => {
        if (isAllOfficesSelected.value) return "";
        return props.offices
            .filter(office => officesSelected.value.includes(office.id) && office.number)
            .map(office => office.number)
            .join(",");
    });

    function readStoredOfficeIds(): number[] {
        try {
            const parsed = JSON.parse(localStorage.getItem(OFFICE_IDS_STORAGE_KEY) ?? "[]");
            return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : [];
        } catch {
            return [];
        }
    }

    // Precedence: shared link, then the user's saved preference, then the session's office,
    // then every office. Anything not in the current office list is dropped so a stale link
    // or preference can't select an office the user cannot see.
    function getInitialOfficeIds(offices: Office[]): number[] {
        const availableIds = offices.map(office => office.id);
        const isAvailable = (id: number) => availableIds.includes(id);

        const urlOfficeIds = parseNumberList(initialUrlFilters[OFFICE_IDS_URL_KEY])?.filter(isAvailable);
        if (urlOfficeIds?.length) return urlOfficeIds;

        const storedOfficeIds = readStoredOfficeIds().filter(isAvailable);
        if (storedOfficeIds.length) return storedOfficeIds;

        if (props.selectedOfficeId !== undefined && isAvailable(props.selectedOfficeId)) {
            return [props.selectedOfficeId];
        }

        return availableIds;
    }

    // The office numbers the grid was last searched with, so closing the dropdown
    // without changing anything does not refetch. Declared before the initialization
    // watcher below because triggerSearch() writes to it during setup.
    let searchedLocationNumbers: string | null = null;

    //// INITIALIZATION (deferred until offices are available)
    let hasInitialized = false;

    watch(
        () => props.offices,
        (offices) => {
            if (hasInitialized || !offices || offices.length === 0) return;

            officesSelected.value = getInitialOfficeIds(offices);
            hasInitialized = true;
            triggerSearch();
        },
        { immediate: true }
    );


    const apiSearchURL = "/transactions/search";

    const computedRowData = computed(() => {
        const params = new URLSearchParams();
        Object.entries(searchFilters.value).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value.toString());
            }
        });

        return params.toString() ? `${apiSearchURL}?${params.toString()}` : apiSearchURL;
    });

    async function exportCsv() {
        try {
            exportError.value = null; // Clear any previous error
            isExporting.value = true;

            const resp = await fetch("/transactions/DownloadCsv", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildExportBody()),
            });

            if (!resp.ok) {
                const errorMessage = `Export failed with status ${resp.status} ${resp.statusText}`;
                exportError.value = errorMessage;

                aiLog("CSVExportError", {
                    status: resp.status,
                    statusText: resp.statusText,
                    filtersUsed: Object.keys(buildExportBody().filters).length,
                    pageSize: buildExportBody().pageable.pageSize
                });

                return;
            }

            const blob = await resp.blob();
            const href = URL.createObjectURL(blob);

            // filename with TODAY’S date (YYYYMMDD)
            const today = new Date()
                .toISOString()          // 2025-05-01T08:00:00.000Z
                .slice(0, 10)           // 2025-05-01
                .replace(/-/g, "");     // 20250501

            Object.assign(document.createElement("a"), {
                href,
                download: `PaymentReport_${today}.csv`,
            }).click();

            aiLog("CSVExportSuccess", {
                filtersUsed: Object.keys(buildExportBody().filters).length,
                pageSize: buildExportBody().pageable.pageSize
            });

            URL.revokeObjectURL(href);
            exportError.value = null; // Clear any previous error
        } catch (err: unknown) {
            if (err instanceof Error) {
                exportError.value = `Export failed: ${err.message}`;
            } else {
                exportError.value = "An unknown error occurred during export.";
            }

            aiLogError(err instanceof Error ? err : new Error("Unknown CSV error"), "ExportCsv", {
                filtersUsed: Object.keys(buildExportBody().filters).length,
                pageSize: buildExportBody().pageable.pageSize
            });
        }
        finally {
            isExporting.value = false; // Reset exporting state
        }
    }

    //// WATCHERS
    // Each pick only persists the selection; the search runs once the dropdown closes.
    watch(officesSelected, (newVal) => {
        localStorage.setItem(OFFICE_IDS_STORAGE_KEY, JSON.stringify(newVal));
        // Every office is the default, so only a narrowed selection goes in the URL.
        syncFiltersToUrl({ [OFFICE_IDS_URL_KEY]: isAllOfficesSelected.value ? "" : newVal });
    });

    function handleOfficeSelectorClose() {
        if (locationNumbers.value === searchedLocationNumbers) return;

        aiLog("OfficeChanged", {
            officeCount: officesSelected.value.length,
            allOffices: isAllOfficesSelected.value
        });

        triggerSearch();
    }


    watch(debouncedQuickSearch, (val) => {
        // apply the filter only when the simple header is visible
        if (!showAdvancedSearch.value) {
            const trimmed = val.trim();
            if (trimmed !== searchFilters.value.transactionNumber) {
                searchFilters.value.transactionNumber = trimmed;
                syncFiltersToUrl({ transactionNumber: trimmed });
                triggerSearch();
            }
        }

        aiLog("QuickSearchUsed", {
            queryLength: val.length
        });

    });


    //// EVENT HANDLERS
    function triggerSearch() {
        isSearching.value = true;
        searchedLocationNumbers = locationNumbers.value;
        // Replace the whole object so computedRowData notices.
        searchFilters.value = {
            ...searchFilters.value,
            locationNumbers: locationNumbers.value,
        };
    }

    function applyAdvancedSearch(filters: SearchFilters) {
        // replace the whole object so Vue’s reactivity notices
        const trimmed = trimFilters({ ...filters });
        searchFilters.value = trimmed;
        // Full shape, empties included, so cleared fields are dropped from the URL.
        syncFiltersToUrl(toUrlFilters(trimmed));
        quickSearch.value = trimmed.transactionNumber ?? '';
        triggerSearch();                          // reuse existing logic
    }

    function handleSearchReset(clearedFilters: Record<string, string> = {}) {
        // Clearing the form must not widen the office scope, so keep the selector's offices.
        searchFilters.value = { ...getDefaultSearchFilters(), locationNumbers: locationNumbers.value };
        // The form emits its all-empty key map so every filter param leaves the URL.
        syncFiltersToUrl(toUrlFilters(clearedFilters));
        quickSearch.value = '';
        isSearching.value = true;
    }

    function initializeGridRef(ref: any) {
        gridRef.value = ref;
    }

    function toggleAdvancedSearch() {
        showAdvancedSearch.value = !showAdvancedSearch.value;

        aiLog("AdvancedSearchToggled", {
            showAdvancedSearch: showAdvancedSearch.value
        });
    }

    function buildExportBody() {
        /* ------- 1. FILTERS ------- */
        const filters: Record<string, string | number | boolean> = {};

        // copy non-empty search values, with the current office selection so the export
        // always matches the grid
        Object.entries({ ...searchFilters.value, locationNumbers: locationNumbers.value }).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) filters[k] = v;
        });

        /* ------- 2. PAGINATION ------- */
        const pageNumber = gridRef.value?.getPage() ?? 1;

        let pageSize =
            totalRecords.value || gridRef.value?.getPageSize?.() || 0;
        if (pageSize === 0) {
            console.warn("⚠️ Page size is 0, defaulting to 1000.");
            pageSize = 1000; // default to 1000 if not set
        }
        const pageable = { pageNumber, pageSize };

        /* ------- 3. SORT ------- */
        let sort = null;
        const sorters = gridRef.value?.getSorters() ?? [];
        if (sorters.length) {
            const s = sorters[0];
            sort = { propertyName: s.field, direction: s.dir.toUpperCase() };
        }

        return { filters, pageable, sort };
    }


</script>

<template>
    <Spinner :show="isExporting">Generating CSV…<br>This could take a moment.</Spinner>
    <page-header variant="simple" :disable-sticky="true" title="Payments">
        <div class="controls-wrapper">
            <OfficeSelector v-model="officesSelected" :offices="props.offices" :show-all-offices="props.showAllOffices" :multiple="true" placeholder="Select Offices" qa-id="payment-history-office-selector" @close="handleOfficeSelectorClose" />

            <div class="search-wrapper" v-if="!showAdvancedSearch">
                <CmnhInput type="search" label="Search" placeholder="Search Payment #" :hide-label="true" v-model="quickSearch" data-qa-id="payment-history-quick-search-input" />
            </div>

            <div>
                <p>Options:</p>
            </div>
            <div class="button-group">
                <button class="small primary cmnh-button" data-qa-id="payment-history-export-csv-button" @click="exportCsv" :disabled="totalRecords === 0" :title="totalRecords === 0 ? 'No records available to export' : ''">Export CSV</button>
                <CmnhButton :label="showAdvancedSearch ? 'Simple Search' : 'Advanced Search'" size="small" data-qa-id="payment-history-toggle-advanced-search-button" @click="toggleAdvancedSearch" />
            </div>
        </div>
    </page-header>

    <Alert v-if="exportError" variant="error" :message="'Something went wrong while exporting your CSV.'" :details="exportError" />

    <PaymentsHistorySearchForm v-if="showAdvancedSearch" :initial-search-filters="searchFilters" @update-search="applyAdvancedSearch" @reset-search="handleSearchReset" :transaction-statuses="props.paymentsData.transactionStatuses" :payment-method-types="props.paymentsData.paymentMethodTypes" :initial-location-numbers="locationNumbers" />

    <!-- Payments Data Grid -->
    <PaymentsHistorySectionGrid :rowData="computedRowData" @search-complete="isSearching = false" @record-count="totalRecords = $event" :is-searching="isSearching" :search="Object.values(searchFilters).filter(v => v).join(' ') || ''" :searchFilters="searchFilters" @set-grid-ref="initializeGridRef" />
</template>
